import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import HeightFillingImage from 'components/Common/HeightFillingImage'
import config from 'constants/config'
import TextLink from 'LuxKit/TextLink'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import { INTL_DAY_MONTH_NAME_SHORT, INTL_DAY_ONLY } from 'constants/dateFormats'
import { getDaysToGoLabel } from './utils'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import useOffer from 'hooks/Offers/useOffer'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'

const ImageRatioContainer = styled.div`
  height: ${rem(68)};
  width: ${rem(68)};

  ${mediaQueryUp.tablet} {
    width: ${rem(164)};
    height: ${rem(94)};
  }
`
interface Props {
  order: App.Order
}

function OfferDetailUpsellSection(props: Props) {
  const { order } = props
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const offerId = order.items[0]?.offerId || order.bedbankItems[0]?.offer.id
  const [offer] = useOffer<App.Offer | App.BedbankOffer>(offerId)
  const checkInDate = order.items[0]?.reservation?.startDate || order.bedbankItems[0]?.checkIn
  const checkOutDate = order.items[0]?.reservation?.endDate || order.bedbankItems[0]?.checkOut
  const duration = order.items[0]?.duration || order.bedbankItems[0]?.duration

  const dateLabel = useMemo(() => {
    if (!checkInDate || !checkOutDate) return ''
    const startDate = dateFormatter(checkInDate, INTL_DAY_ONLY)
    const endDate = dateFormatter(checkOutDate, INTL_DAY_MONTH_NAME_SHORT)

    if (!startDate || !endDate) return ''

    return intl.formatMessage(
      {
        id: 'ZHTH8iOdJ7CJ8460Hf_C',
        defaultMessage: '{startDate} - {endDate}',
        description: 'Short stay date range label on the confirmation upsell section',
      },
      {
        startDate,
        endDate,
      },
    )
  }, [checkInDate, checkOutDate, dateFormatter, intl])

  const daysToGoLabel = getDaysToGoLabel(intl, checkInDate, duration)

  if (!offer?.property?.name) return null

  return (<Group direction="horizontal" gap={16} tabletGap={24} horizontalAlign="space-between" tabletDirection="horizontal-reverse" tabletHorizontalAlign="start">
    <Group direction="vertical" gap={4} tabletGap={8}>
      {daysToGoLabel && <LabelGroup>
        <UrgencyLabel variant="speed">{daysToGoLabel}</UrgencyLabel>
      </LabelGroup>}
      <HeadingTextBlock variant="heading2">{offer.property.name}</HeadingTextBlock>
      <BodyText variant="medium">
        <FormattedMessage
          id="CDON6bPf8UKK4pBmY-iD"
          defaultMessage="{dateLabel} · <link>Manage booking</link>"
          description="Date label with link to manage booking on confirmation page"
          values={{
            dateLabel,
            link: (chunks) => <TextLink
              to={config.agentHub.isEnabled ? undefined : `/account/my-escapes/order/${order.id}`}
              href={config.agentHub.isEnabled ? `${config.AGENT_PORTAL_URL}/bookings` : undefined}
              target={config.agentHub.isEnabled ? '_blank' : undefined}
              data-testid="manage-booking"
            >{chunks}
            </TextLink>,
          }}
        />
      </BodyText>
    </Group>
    {offer.image.id && (
      <ImageRatioContainer>
        <HeightFillingImage fit="center" id={offer.image.id} />
      </ImageRatioContainer>
    )}
  </Group>
  )
}

export default OfferDetailUpsellSection
